import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import {
  API,
  ColorDefinitionDTO,
  PlanningDTO,
  PlanningSubmissionDTO,
  ScheduledCommercialDTO,
  TemplateDataSetEntryDTO,
} from '../../../services/api/api';
import { MsOptions, SbSplitButton, TableWrapperOptions } from '../../../services/global/global-interfaces';
import { UtilService } from '../../../services/util.service';
import { ErrorService } from '../../../services/error.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { TranslateService } from '@ngx-translate/core';
import { TagService } from '../../../services/tag.service';
import { DiscussionService } from '../../../services/discussion.service';
import { ToasterService, ToastType } from '../../../services/toaster/toaster.service';
import { UserService } from '../../../services/user.service';
import { ActivatedRoute, Params } from '@angular/router';
import { PermissionService } from '../../../services/permission.service';
import { NavigationService } from '@ngx-seatback/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { RequestClass } from '../../../services/global/RequestClass';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { SbSplitButtonItem } from '@ngx-seatback/controls';

type SplitButtonAction = 'management' | 'plannings' | 'accept' | 'reject';

@Component({
  selector: 'sb-comp-partial-planning',
  templateUrl: './sb-partial-planning.component.html',
  styleUrls: ['./sb-partial-planning.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SbPartialPlanningComponent extends RequestClass {
  private readonly api = inject(API);
  public readonly util = inject(UtilService);
  private readonly errorHandler = inject(ErrorService);
  private readonly modal = inject(ModalService);
  public readonly translate = inject(TranslateService);
  public readonly tagService = inject(TagService);
  public readonly discussionService = inject(DiscussionService);
  private readonly toastHandler = inject(ToasterService);
  public readonly userStatus = inject(UserService);
  public readonly activatedRoute = inject(ActivatedRoute);
  public readonly permissionService = inject(PermissionService);
  private readonly navigationService = inject(NavigationService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() planningID: number;

  submissions: PlanningSubmissionDTO[] = [];
  planning: PlanningDTO;

  scheduledCommercials: ScheduledCommercialDTO[] = [];
  selectedSubmission: PlanningSubmissionDTO = null;

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .ds_cm_Planning_retrieveById(this.planningID)
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: planningData => {
          this.planning = planningData;
          this.submissions = this.buildSplitButtons(planningData.submissions);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  deletePlanning(submission: PlanningSubmissionDTO) {
    this.modal
      .confirm({
        title: this.translate.instant('lang.plannings.deletePlanning'),
        message: this.translate.instant('lang.plannings.deletePlanningQuestion', { planning: submission.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .ds_cm_Planning_deleteById(submission.id)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.splicePlanning(submission);
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      });
  }

  splicePlanning(submission: PlanningSubmissionDTO) {
    const index = this.submissions.findIndex(x => x.id === submission.id);
    if (index !== -1) {
      this.submissions.splice(index, 1);
    }
  }

  acceptPlanning(submission: PlanningSubmissionDTO) {
    this.modal
      .confirm({
        title: this.translate.instant('lang.bookingEditor.modal.acceptPlanningHeadline', { planningName: submission.name }),
        message: this.translate.instant('lang.bookingEditor.modal.acceptPlanningWithoutDelete', { planningName: submission.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.loading.set(true);
          this.api
            .ds_cm_Planning_acceptById(this.planningID, submission.id)
            .pipe(
              take(1),
              finalize(() => {
                this.loading.set(false);
              })
            )
            .subscribe({
              next: () => {
                this.navigationService.navigateToPoint(this.util.RoutingID.plannings);
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      });
  }

  rejectPlanning(submission: PlanningSubmissionDTO) {
    this.modal
      .confirm({
        title: this.translate.instant('lang.bookingEditor.modal.rejectPlanningHeadline'),
        message: this.translate.instant('lang.bookingEditor.modal.rejectPlanning'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.loading.set(true);
          this.api
            .ds_cm_Planning_withdrawSubmissionById(this.planningID, submission.id)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastHandler.toast(ToastType.SUCCESS, this.translate.instant('lang.bookingEditor.rejectedPlanning'));
                this.refreshFromServer();
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      });
  }

  buildSplitButtons<
    T extends {
      id: any;
      selectedRequirementsCount: number;
      selectedCommercialsCount: number;
      selectedCampaignsCount: number;
      selectedContentSharesCount: number;
    },
  >(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return {
        ...item,
        sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[],
        extraSbButtons: [] as SbSplitButtonItem<SplitButtonAction>[],
      };
    });
    result.forEach(item => {
      const planinngsDisabled: boolean =
        (item?.selectedRequirementsCount ?? 0) > 0 ||
        (item?.selectedCommercialsCount ?? 0) > 0 ||
        (item?.selectedCampaignsCount ?? 0) > 0 ||
        (item?.selectedContentSharesCount ?? 0) === 0;
      const managementDisabled: boolean =
        !(
          (item?.selectedRequirementsCount ?? 0) > 0 ||
          (item?.selectedCommercialsCount ?? 0) > 0 ||
          (item?.selectedCampaignsCount ?? 0) > 0
        ) || (item?.selectedContentSharesCount ?? 0) > 0;
      if (this.permissionService.hasPermission(this.permissionService.Permission.planning_list)) {
        item.sbButtons.push({
          disabled: planinngsDisabled,
          icon: 'plannings',
          tooltip: 'lang.planningsList.headline',
          link: RoutingID.planning,
          pathParams: [{ id: 'planningID', value: this.planningID }],
          queryParams: { submission: item?.id },
        });
      }
      if (this.permissionService.hasPermission(this.permissionService.Permission.content_share_list)) {
        item.sbButtons.push({
          disabled: managementDisabled,
          icon: 'management',
          tooltip: 'lang.globals.contentShares',
          link: RoutingID.contentShare,
          queryParams: { submission: item?.id, planning: this.planningID },
        });
      }
      if (this.permissionService.hasPermission(this.permissionService.Permission.planning_accept)) {
        item.extraSbButtons.push({
          icon: 'boxChecked',
          iconClass: 'hover:!tw-bg-sbGreen-500 *:tw-text-sbGreen-500',
          action: 'accept',
          tooltip: 'lang.globals.accept',
        });
      }
      if (this.permissionService.hasPermission(this.permissionService.Permission.planning_submit)) {
        item.extraSbButtons.push({
          icon: 'error',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          action: 'reject',
          tooltip: 'lang.globals.reject',
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(submission: PlanningSubmissionDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'accept':
        this.acceptPlanning(submission);
        break;
      case 'reject':
        this.rejectPlanning(submission);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }
}
